import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import { setTokenProvider } from './lib/api';
import { getAuthTokenRequestOptions } from './lib/authToken';
import { ThemeModeProvider } from './context/ThemeModeContext';
import Layout from './components/Layout';
import EntityCrudPage from './components/EntityCrudPage';
import Dashboard from './pages/Dashboard';
import ApiDashboard from './pages/ApiDashboard';
import ApiKeys from './pages/ApiKeys';
// swagger-ui-react ships ~700KB gzipped of CSS+JS; keep it out of the main bundle.
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
import Assessments from './pages/Assessments';
import AssessmentDetail from './pages/AssessmentDetail';
import ComplianceLookup from './pages/ComplianceLookup';
import ComplianceDiff from './pages/ComplianceDiff';
import Deadlines from './pages/Deadlines';
import SavedAssessments from './pages/SavedAssessments';
import Validation from './pages/Validation';
import AuditLog from './pages/AuditLog';
import Changelog from './pages/Changelog';
import { ENTITY_CONFIGS } from './pages/entities/configs';

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    setTokenProvider(() => getAccessTokenSilently(getAuthTokenRequestOptions()));
  }, [getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ThemeModeProvider>
      <BrowserRouter>
        <AuthGate>
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/api/dashboard" element={<ApiDashboard />} />
              <Route path="/lookup" element={<ComplianceLookup />} />
              <Route path="/diff" element={<ComplianceDiff />} />
              <Route path="/deadlines" element={<Deadlines />} />
              <Route path="/keys" element={<ApiKeys />} />
              <Route
                path="/docs"
                element={
                  <Suspense
                    fallback={
                      <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                      </Box>
                    }
                  >
                    <ApiDocs />
                  </Suspense>
                }
              />
              <Route path="/assessments" element={<Assessments />} />
              <Route path="/assessments/:id" element={<AssessmentDetail />} />
              <Route path="/saved-assessments" element={<SavedAssessments />} />
              <Route path="/validation" element={<Validation />} />
              <Route path="/audit" element={<AuditLog />} />
              <Route path="/changelog" element={<Changelog />} />
              {Object.entries(ENTITY_CONFIGS).map(([slug, config]) => (
                <Route
                  key={slug}
                  path={`/entities/${slug}`}
                  element={<EntityCrudPage {...config} />}
                />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </AuthGate>
      </BrowserRouter>
    </ThemeModeProvider>
  );
}
